import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faUsers, faSpinner } from "@fortawesome/free-solid-svg-icons";

const UserFilter = ({
  users = [],
  selectedUser,
  onUserChange,
  loading = false,
  userType = "User",
  placeholder = "Select user...",
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Debug logging
  console.log("🎯 UserFilter props:", {
    users,
    usersLength: users?.length,
    selectedUser,
    loading,
    userType,
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".user-filter-dropdown")) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleUserSelect = (userId) => {
    onUserChange(userId);
    setIsOpen(false);
  };

  const selectedUserName = selectedUser
    ? users.find((user) => user.id === selectedUser)?.name || "Unknown User"
    : `All ${userType}s`;

  return (
    <div className="user-filter-container" style={{ marginBottom: "20px" }}>
      <style>
        {`
          .user-filter-dropdown {
            position: relative;
            display: inline-block;
            min-width: 200px;
          }

          .user-filter-button {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 10px 15px;
            background: white;
            border: 2px solid #e9ecef;
            border-radius: 8px;
            cursor: pointer;
            font-size: 14px;
            font-weight: 500;
            color: #495057;
            transition: all 0.2s ease;
            width: 100%;
            box-sizing: border-box;
          }

          .user-filter-button:hover {
            border-color: #4CAF50;
            box-shadow: 0 2px 8px rgba(76, 175, 80, 0.15);
          }

          .user-filter-button.open {
            border-color: #4CAF50;
            box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
          }

          .user-filter-content {
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .user-filter-dropdown-menu {
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            background: white;
            border: 2px solid #4CAF50;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            z-index: 1000;
            max-height: 300px;
            overflow-y: auto;
            margin-top: 2px;
          }

          .user-filter-option {
            padding: 12px 15px;
            cursor: pointer;
            font-size: 14px;
            color: #495057;
            border-bottom: 1px solid #f8f9fa;
            transition: background-color 0.2s ease;
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .user-filter-option:hover {
            background-color: #f8f9fa;
          }

          .user-filter-option.selected {
            background-color: #4CAF50;
            color: white;
          }

          .user-filter-option:last-child {
            border-bottom: none;
          }

          .user-filter-arrow {
            transition: transform 0.2s ease;
          }

          .user-filter-arrow.rotated {
            transform: rotate(180deg);
          }

          @media (max-width: 768px) {
            .user-filter-dropdown {
              min-width: 150px;
            }
            
            .user-filter-button {
              font-size: 12px;
              padding: 8px 12px;
            }
            
            .user-filter-option {
              font-size: 12px;
              padding: 10px 12px;
            }
          }
        `}
      </style>

      <div className="user-filter-dropdown">
        <button
          className={`user-filter-button ${isOpen ? "open" : ""}`}
          onClick={() => setIsOpen(!isOpen)}
          disabled={loading}
        >
          <div className="user-filter-content">
            <FontAwesomeIcon
              icon={loading ? faSpinner : selectedUser ? faUser : faUsers}
              spin={loading}
            />
            <span>
              {loading
                ? "Loading..."
                : users && users.length > 0
                ? selectedUserName
                : "No users available"}
            </span>
          </div>
          <span className={`user-filter-arrow ${isOpen ? "rotated" : ""}`}>
            ▼
          </span>
        </button>

        {isOpen && !loading && users && users.length > 0 && (
          <div className="user-filter-dropdown-menu">
            <div
              className={`user-filter-option ${
                !selectedUser ? "selected" : ""
              }`}
              onClick={() => handleUserSelect(null)}
            >
              <FontAwesomeIcon icon={faUsers} />
              <span>All {userType}s</span>
            </div>
            {users.map((user) => (
              <div
                key={user.id}
                className={`user-filter-option ${
                  selectedUser === user.id ? "selected" : ""
                }`}
                onClick={() => handleUserSelect(user.id)}
              >
                <FontAwesomeIcon icon={faUser} />
                <span>{user.name}</span>
              </div>
            ))}
          </div>
        )}

        {/* Show message when no users available */}
        {isOpen && !loading && (!users || users.length === 0) && (
          <div className="user-filter-dropdown-menu">
            <div
              className="user-filter-option"
              style={{ color: "#666", fontStyle: "italic" }}
            >
              No {userType}s available
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserFilter;
